import { Component, EventEmitter, Input, Output, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { KeycloakService } from 'keycloak-angular';
import { OgcStacApiService, StacItem, StacAsset } from '../../../services/ogc-stac-api.service';
import { ToasterService } from '../../../../services/toaster.service';

@Component({
  selector: 'app-ogc-stac-data',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ogc-stac-data.component.html',
  styleUrl: './ogc-stac-data.component.scss',
})
export class OgcStacDataComponent implements OnInit {
  @Input() resourceItemData: {
    id?: string;
    label?: string;
    fileFormat?: string;
    stacServerBaseUrl?: string;
  } | null = null;

  @Output() closeRequested = new EventEmitter<void>();

  private ogcStacApi = inject(OgcStacApiService);
  private keycloak = inject(KeycloakService);
  private toasterService = inject(ToasterService);

  stacData: { features?: StacItem[] } | null = null;
  isLoading = true;
  isDownloading = false;
  errorMessage: string | null = null;
  expandedItems = new Set<number>();
  searchQuery = '';

  private get collectionId(): string {
    return this.resourceItemData?.id ?? '';
  }

  get features(): StacItem[] {
    const data = this.stacData as { features?: StacItem[]; items?: StacItem[] } | null;
    return data?.features ?? data?.items ?? [];
  }

  /** Features filtered by search query (title, description, id, type, properties) */
  get filteredFeatures(): StacItem[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return this.features;
    return this.features.filter((f) => this.featureMatchesSearch(f, q));
  }

  private featureMatchesSearch(feature: StacItem, q: string): boolean {
    const title = this.getFeatureTitle(feature).toLowerCase();
    const desc = (this.getFeatureDescription(feature) ?? '').toLowerCase();
    const id = (feature.id ?? '').toLowerCase();
    const type = (this.getFeatureType(feature) ?? '').toLowerCase();
    if (title.includes(q) || desc.includes(q) || id.includes(q) || type.includes(q)) return true;
    const props = this.getFeatureDisplayProperties(feature);
    return props.some(([k, v]) => k.toLowerCase().includes(q) || String(v).toLowerCase().includes(q));
  }

  async ngOnInit(): Promise<void> {
    const id = this.resourceItemData?.id;
    if (!id) {
      this.errorMessage = 'Collection ID is not available.';
      this.isLoading = false;
      return;
    }
    try {
      const token = await this.keycloak.getToken().catch(() => undefined);
      this.stacData = await this.ogcStacApi.fetchCollectionItems(id, token ?? undefined);
    } catch (err: unknown) {
      const msg =
        (err as { error?: { detail?: string }; message?: string })?.error?.detail ??
        (err as { message?: string })?.message ??
        (err as { statusText?: string })?.statusText ??
        'Failed to fetch STAC items.';
      this.errorMessage = typeof msg === 'string' ? msg : 'Failed to fetch STAC items.';
    } finally {
      this.isLoading = false;
    }
  }

  /** First asset from feature (for description, type, bbox from primary asset) */
  getFirstAsset(feature: StacItem): StacAsset | undefined {
    if (!feature?.assets || typeof feature.assets !== 'object') return undefined;
    const key = Object.keys(feature.assets)[0];
    const asset = key ? feature.assets[key] : undefined;
    return asset && typeof asset === 'object' ? (asset as StacAsset) : undefined;
  }

  getFeatureTitle(feature: StacItem): string {
    if (!feature) return 'Untitled';
    const propTitle = feature.properties?.['title'];
    if (propTitle != null && propTitle !== '') return String(propTitle);
    const asset = this.getFirstAsset(feature);
    if (asset?.title) return String(asset.title);
    return feature.id ?? 'Untitled';
  }

  getFeatureDescription(feature: StacItem): string | null {
    const asset = this.getFirstAsset(feature);
    const desc = asset?.description ?? feature.properties?.['description'];
    if (desc == null || desc === '') return null;
    return String(desc);
  }

  getFeatureType(feature: StacItem): string | null {
    const asset = this.getFirstAsset(feature);
    const t = asset?.type;
    if (t == null || t === '') return null;
    return String(t);
  }

  /** Formatted bbox from asset or feature (e.g. "[minX, minY, maxX, maxY]") */
  getFeatureBbox(feature: StacItem): string | null {
    const asset = this.getFirstAsset(feature);
    const bbox = (asset?.bbox as number[] | undefined) ?? (feature.bbox as number[] | undefined);
    if (!Array.isArray(bbox) || bbox.length < 4) {
      const p = feature.properties as Record<string, unknown> | undefined;
      if (p && typeof p['bounds_min_x'] === 'number' && typeof p['bounds_min_y'] === 'number' &&
          typeof p['bounds_max_x'] === 'number' && typeof p['bounds_max_y'] === 'number') {
        return `[${(p['bounds_min_x']).toFixed(4)}, ${(p['bounds_min_y']).toFixed(4)}, ${(p['bounds_max_x']).toFixed(4)}, ${(p['bounds_max_y']).toFixed(4)}]`;
      }
      return null;
    }
    return `[${bbox.slice(0, 4).map((n) => Number(n).toFixed(4)).join(', ')}]`;
  }

  /** Key-value pairs for properties section (exclude noisy/internal keys) */
  getFeatureDisplayProperties(feature: StacItem): [string, string][] {
    const p = feature.properties;
    if (!p || typeof p !== 'object') return [];
    const skip = new Set(['title', 'description', 'bounds_min_x', 'bounds_min_y', 'bounds_max_x', 'bounds_max_y']);
    const entries: [string, string][] = [];
    for (const [k, v] of Object.entries(p)) {
      if (skip.has(k)) continue;
      if (v == null) entries.push([k, '—']);
      else if (typeof v === 'object' && !Array.isArray(v) && (v as object).constructor?.name === 'Object') continue;
      else entries.push([k, String(v)]);
    }
    return entries.slice(0, 12);
  }

  /** Human-readable label for property key (snake_case → Title case) */
  formatPropertyKey(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  /** Index in original features array (stable across search filter) */
  getFeatureIndex(feature: StacItem): number {
    return this.features.indexOf(feature);
  }

  isPropertiesExpanded(feature: StacItem): boolean {
    return this.expandedItems.has(this.getFeatureIndex(feature));
  }

  toggleProperties(feature: StacItem): void {
    const idx = this.getFeatureIndex(feature);
    if (this.expandedItems.has(idx)) {
      this.expandedItems.delete(idx);
    } else {
      this.expandedItems.add(idx);
    }
    this.expandedItems = new Set(this.expandedItems);
  }

  async downloadFeature(feature: StacItem): Promise<void> {
    if (!feature?.id) {
      this.toasterService.error('Invalid feature data');
      return;
    }
    this.isDownloading = true;
    try {
      const token = await this.keycloak.getToken().catch(() => undefined);
      const blob = await this.ogcStacApi.downloadStacAsset(
        this.collectionId,
        feature.id,
        token ?? undefined
      );
      const title = this.getFeatureTitle(feature);
      const fileFormat = (this.resourceItemData?.fileFormat as string)?.toLowerCase() ?? 'tiff';
      const ext = fileFormat.includes('tif') ? 'tiff' : fileFormat || 'tiff';
      const now = new Date();
      const day = now.getDate().toString().padStart(2, '0');
      const month = now.toLocaleString('en-US', { month: 'short' });
      const year = now.getFullYear();
      const sanitized = String(title).replace(/[^a-z0-9]/gi, '_').toLowerCase().substring(0, 50);
      const filename = `${sanitized}_${day}${month}${year}.${ext}`;
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'image/tiff' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      this.toasterService.success('File downloaded successfully');
    } catch (err) {
      console.error('Error downloading STAC asset:', err);
      this.toasterService.error('Failed to download file. Please try again.');
    } finally {
      this.isDownloading = false;
    }
  }

  downloadAll(): void {
    if (!this.stacData) return;
    const blob = new Blob([JSON.stringify(this.stacData, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stac-items-${this.resourceItemData?.id ?? 'data'}-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  closeModal(): void {
    this.closeRequested.emit();
  }
}
